import { type FormEvent, type ReactNode, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useLogin, useMe } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Alert, Button, Field, PasswordField } from '../components/ui.js';
import { Wordmark } from '../components/Logo.js';

/** Centred card used by the sign-in and sign-up screens. */
export function AuthShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-8">
      <Wordmark className="h-12 w-auto" />
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-sm ring-1 ring-secondary/5">
        <h1 className="mb-6 text-xl font-semibold">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { data: user } = useMe();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (user) return <Navigate to={from} replace />;

  // A prior attempt asked for a second factor: show the code field.
  const mfaError = login.error instanceof ApiError ? login.error.code : null;
  const mfaStep = mfaError === 'mfa_required' || mfaError === 'mfa_invalid';

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    login.mutate(
      { email, password, ...(mfaStep && code ? { code } : {}) },
      { onSuccess: () => void navigate(from, { replace: true }) },
    );
  };

  const error =
    mfaError === 'invalid_credentials'
      ? 'That email and password do not match.'
      : mfaError === 'account_blocked'
        ? 'This account has been blocked. Please contact Aldemis.'
        : mfaError === 'mfa_invalid'
          ? 'That authentication code is not valid.'
          : login.isError && !mfaStep
            ? 'Something went wrong. Please try again.'
            : null;

  return (
    <AuthShell title="Sign in">
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        {error ? <Alert>{error}</Alert> : null}
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {mfaStep ? (
          <Field
            label="Authentication code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        ) : null}
        <Button type="submit" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : mfaStep ? 'Verify code' : 'Sign in'}
        </Button>
      </form>
      <p className="mt-4 text-sm opacity-70">
        Aldemis Foundry is invite-only. If you’re expecting access, use the link in your invitation
        email.
      </p>
    </AuthShell>
  );
}
